import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const REDBG = "#FDECEC";
const RED = "#DC2626";
const GREEN = "#16A34A";
const GREENBG = "#E7F6EC";

const Community = () => {
  const navigate = useNavigate();
  const [communities, setCommunities] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");

  const load = (q = "") =>
    apiInstanceFetch
      .get(`community/admin/all?start=0&limit=100${q ? `&search=${encodeURIComponent(q)}` : ""}`)
      .then((r) => {
        setCommunities(r.communities || []);
        setTotal(r.total || 0);
      })
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(search.trim());
  };

  const remove = (c) => {
    if (!window.confirm(`Take down community "${c.name}"? It will be hidden from all users.`)) return;
    setBusy(c._id);
    apiInstanceFetch
      .delete(`community/admin/community?communityId=${c._id}`)
      .then(() => setCommunities((prev) => prev.map((x) => (x._id === c._id ? { ...x, isActive: false } : x))))
      .catch(() => {})
      .finally(() => setBusy(""));
  };

  const creatorName = (c) => {
    const u = c.createdBy;
    if (!u) return "Unknown";
    return ((u.firstName || "") + " " + (u.lastName || "")).trim() || u.email || "Unknown";
  };

  return (
    <div className="mainCategory">
      <Title name="Community" />

      <div className="d-flex align-items-center" style={{ gap: 10, margin: "16px 0" }}>
        <i className="fa-solid fa-users" style={{ color: GOLD }} />
        <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>All communities ({total})</span>
        <form onSubmit={onSearch} style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="form-control form-control-sm"
            style={{ border: `1px solid ${HAIRLINE}`, minWidth: 220 }}
          />
          <button className="btn btn-sm" style={{ border: `1px solid ${HAIRLINE}`, color: INK, fontWeight: 600 }}>
            Search
          </button>
        </form>
      </div>

      <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "hidden" }}>
        <table className="table mb-0" style={{ fontSize: 13.5 }}>
          <thead style={{ background: CREAM }}>
            <tr style={{ color: MUTED }}>
              <th>Community</th>
              <th>Creator</th>
              <th>Members</th>
              <th>Posts</th>
              <th>Created</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {communities.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 32, color: MUTED }}>
                  No communities yet
                </td>
              </tr>
            ) : (
              communities.map((c) => (
                <tr key={c._id}>
                  <td>
                    <div className="d-flex align-items-center" style={{ gap: 10 }}>
                      {c.image ? (
                        <img
                          src={c.image}
                          alt=""
                          width={38}
                          height={38}
                          style={{ borderRadius: 8, objectFit: "cover" }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: 38,
                            height: 38,
                            borderRadius: 8,
                            background: CREAM,
                            border: `1px solid ${HAIRLINE}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: GOLD,
                          }}
                        >
                          <i className="fa-solid fa-users" />
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 700, color: INK }}>{c.name}</div>
                        <div
                          style={{
                            fontSize: 11.5,
                            color: MUTED,
                            maxWidth: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ color: INK }}>{creatorName(c)}</td>
                  <td style={{ color: INK }}>{c.membersCount}</td>
                  <td style={{ color: INK }}>{c.postsCount}</td>
                  <td style={{ color: MUTED }}>{dayjs(c.createdAt).format("DD MMM YYYY")}</td>
                  <td>
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        padding: "2px 9px",
                        borderRadius: 20,
                        background: c.isActive ? GREENBG : REDBG,
                        color: c.isActive ? GREEN : RED,
                      }}
                    >
                      {c.isActive ? "Active" : "Taken down"}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex" style={{ gap: 6 }}>
                      <button
                        onClick={() => navigate(`/admin/community/${c._id}`, { state: { community: c } })}
                        className="btn btn-sm"
                        style={{ border: `1px solid ${HAIRLINE}`, color: INK, fontWeight: 600 }}
                      >
                        <i className="fa-solid fa-eye" style={{ marginRight: 5 }} />
                        View
                      </button>
                      {c.isActive && (
                        <button
                          disabled={busy === c._id}
                          onClick={() => remove(c)}
                          className="btn btn-sm"
                          style={{ border: "none", background: REDBG, color: RED, fontWeight: 700 }}
                        >
                          <i className="fa-solid fa-trash" style={{ marginRight: 5 }} />
                          Take down
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Community;
